import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import api from '../services/api';
import MultiDayDateRangeFields, {
  getDefaultMultiDayRange,
  MAX_MULTI_DAY_RANGE,
} from './MultiDayDateRangeFields';
import { resolveMultiDayDates } from '../utils/dateHelpers';
import { useDateFormatter } from '../hooks/useDateFormatter';

interface CopyTimeEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (copiedCount: number) => void;
  entry: {
    id: string;
    workedDate: string;
    customerId: string;
    engagementId: string;
    groupId: string;
    type: string;
    timeSpent: number;
    description: string;
    task?: string;
    Customer?: { accountName: string };
    Engagement?: { name: string };
    Group?: { name: string };
  } | null;
}

const CopyTimeEntryModal: React.FC<CopyTimeEntryModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  entry,
}) => {
  const { formatDateOnly, formatDateForInput, getToday } = useDateFormatter();
  const [rangeStartDate, setRangeStartDate] = useState('');
  const [rangeEndDate, setRangeEndDate] = useState('');
  const [includeWeekends, setIncludeWeekends] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState<string>('');

  const originalDate = useMemo(() => {
    if (!entry) return '';
    return formatDateForInput(entry.workedDate);
  }, [entry, formatDateForInput]);

  const maxCopyDate = getToday();

  useEffect(() => {
    if (isOpen && entry && maxCopyDate) {
      const defaults = getDefaultMultiDayRange(maxCopyDate);
      setRangeStartDate(defaults.start);
      setRangeEndDate(defaults.end);
      setIncludeWeekends(false);
      setSubmitError('');
    } else if (isOpen && entry) {
      setRangeStartDate('');
      setRangeEndDate('');
      setIncludeWeekends(false);
      setSubmitError('');
    }
  }, [isOpen, entry, maxCopyDate]);

  const resolvedDates = useMemo(
    () =>
      resolveMultiDayDates({
        rangeStartDate,
        rangeEndDate,
        maxDate: maxCopyDate,
        includeWeekends,
        excludeDate: originalDate,
      }),
    [rangeStartDate, rangeEndDate, maxCopyDate, includeWeekends, originalDate]
  );

  const validate = () => {
    if (!entry) return false;

    if (!rangeStartDate || !rangeEndDate) {
      setSubmitError('Select a start date and end date.');
      return false;
    }

    if (rangeStartDate > rangeEndDate) {
      setSubmitError('End date must be on or after the start date.');
      return false;
    }

    if (maxCopyDate && rangeEndDate > maxCopyDate) {
      setSubmitError('End date cannot be after today.');
      return false;
    }

    if (resolvedDates.length === 0) {
      setSubmitError(
        includeWeekends
          ? 'No eligible dates in the selected range.'
          : 'No weekdays in the selected range. Check the range or enable "Add Entries for Weekend".'
      );
      return false;
    }

    if (resolvedDates.length > MAX_MULTI_DAY_RANGE) {
      setSubmitError(`You can copy to at most ${MAX_MULTI_DAY_RANGE} dates at a time.`);
      return false;
    }

    setSubmitError('');
    return true;
  };

  const handleCopy = async () => {
    if (!entry || !validate()) {
      return;
    }

    setSaving(true);
    setSubmitError('');

    try {
      const response = await api.post(`/time-entries/${entry.id}/copy`, {
        dates: resolvedDates,
      });

      const copiedCount = response.data?.created || resolvedDates.length;
      onSuccess(copiedCount);
      onClose();
    } catch (error: any) {
      console.error('Error copying time entry:', error);
      setSubmitError(error.response?.data?.message || 'Failed to copy time entry');
    } finally {
      setSaving(false);
    }
  };

  if (!entry) {
    return null;
  }

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-3xl sm:p-6">
                <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                  <button
                    type="button"
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    onClick={onClose}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:ml-0 sm:mt-0 sm:text-left w-full">
                    <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-gray-900">
                      Copy Time Entry
                    </Dialog.Title>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Select a date range to create copies of this time entry across multiple days.
                      </p>
                    </div>

                    <div className="mt-6 grid gap-4 rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <span className="block text-gray-500">Original Date</span>
                          <p className="font-medium text-gray-900">{formatDateOnly(entry.workedDate)}</p>
                        </div>
                        <div>
                          <span className="block text-gray-500">Hours</span>
                          <p className="font-medium text-gray-900">{entry.timeSpent} minutes</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <span className="block text-gray-500">Type</span>
                          <p className="font-medium text-gray-900">{entry.type}</p>
                        </div>
                        <div>
                          <span className="block text-gray-500">Group</span>
                          <p className="font-medium text-gray-900">{entry.Group?.name || '-'}</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <span className="block text-gray-500">Customer</span>
                          <p className="font-medium text-gray-900">{entry.Customer?.accountName || '-'}</p>
                        </div>
                        <div>
                          <span className="block text-gray-500">Engagement</span>
                          <p className="font-medium text-gray-900">{entry.Engagement?.name || '-'}</p>
                        </div>
                      </div>
                      {entry.description && (
                        <div>
                          <span className="block text-gray-500">Description</span>
                          <p className="font-medium text-gray-900">{entry.description}</p>
                        </div>
                      )}
                      {entry.task && (
                        <div>
                          <span className="block text-gray-500">Task</span>
                          <p className="font-medium text-gray-900">{entry.task}</p>
                        </div>
                      )}
                    </div>

                    <div className="mt-6">
                      <h4 className="text-sm font-medium text-gray-900">Date range</h4>
                      <p className="mt-1 text-xs text-gray-500">
                        The original entry date ({formatDateOnly(entry.workedDate)}) is skipped. Dates after today cannot be selected.
                      </p>

                      <div className="mt-4">
                        <MultiDayDateRangeFields
                          rangeStartDate={rangeStartDate}
                          rangeEndDate={rangeEndDate}
                          includeWeekends={includeWeekends}
                          onRangeStartChange={(value) => {
                            setRangeStartDate(value);
                            setSubmitError('');
                          }}
                          onRangeEndChange={(value) => {
                            setRangeEndDate(value);
                            setSubmitError('');
                          }}
                          onIncludeWeekendsChange={(checked) => {
                            setIncludeWeekends(checked);
                            setSubmitError('');
                          }}
                          maxDate={maxCopyDate}
                          excludeDate={originalDate}
                          error={submitError}
                        />
                      </div>
                    </div>

                    <div className="mt-6 flex justify-end gap-3">
                      <button
                        type="button"
                        onClick={onClose}
                        className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleCopy}
                        disabled={saving || resolvedDates.length === 0}
                        className="inline-flex justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {saving
                          ? 'Copying...'
                          : resolvedDates.length === 0
                            ? 'Copy Entries'
                            : `Copy ${resolvedDates.length} ${resolvedDates.length === 1 ? 'Entry' : 'Entries'}`}
                      </button>
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default CopyTimeEntryModal;
